<template>
  <!--
    登入對話框（2026-10-21）：在網站裡按「登入」、或點到要登入才能看的頁面時原地跳出來，
    登入完留在原本的頁面（或繼續前往原本要去的頁面）。開啟：utils/loginDialog.js 的 openLoginDialog。

    內容就是登入頁用的元件（SignIn、SignUp 用 mode="dialog"），兩邊共用同一份表單與「記住帳號密碼」：
      登入 ⇄ 註冊 → 驗證信已寄出；忘記密碼 → 寄出重設信。收信之後（設定密碼、重設密碼）是信裡的連結，走登入頁。
    登入頁還在：社群／AI 站導過來、直接打開網址、子站自訂網域、社群邀請連結、手機的 Google 登入轉回來。
  -->
  <v-dialog :value="show" max-width="440" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable @input="onInput">
    <v-card class="login-dialog">
      <v-btn icon class="login-dialog__close" aria-label="關閉" @click="dismiss">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-card-text class="login-dialog__body">
        <template v-if="show">
          <SignIn
            v-if="view === 'SignIn'"
            mode="dialog"
            :after="after"
            :preset-email="dialogState.email"
            :blank="dialogState.blank"
            :notice="dialogState.notice"
            @signed-in="onSignedIn"
            @switch="onSwitch"
          />
          <SignUp v-else-if="view === 'SignUp'" mode="dialog" :after="after" @signed-in="onSignedIn" @switch="onSwitch" />
          <VerifyEmail v-else-if="view === 'VerifyEmail'" @switch="onSwitch" />
          <ResetPwd v-else-if="view === 'ResetPwd'" @switch="onSwitch" />
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import SignIn from "@/components/SignIn/SignIn.vue";
import SignUp from "@/components/SignIn/SignUp.vue";
import VerifyEmail from "@/components/SignIn/VerifyEmail.vue";
import ResetPwd from "@/components/SignIn/ResetPwd.vue";
import { LOGIN_FLOW_ROUTES } from "@/utils/loginDialog";

// 這幾個在對話框裡切換；其他的（例如設定密碼）走登入頁
const DIALOG_VIEWS = ["SignIn", "SignUp", "VerifyEmail", "ResetPwd"];

export default {
  name: "LoginDialog",
  components: { SignIn, SignUp, VerifyEmail, ResetPwd },
  computed: {
    show() {
      return this.$store.state.loginDialog.show;
    },
    after() {
      return this.$store.state.loginDialog.after;
    },
    view() {
      return this.$store.state.loginDialog.view || "SignIn";
    },
    dialogState() {
      return this.$store.state.loginDialog;
    },
  },
  watch: {
    // 換到登入頁（例如這台被別的裝置登出、被帶到登入頁）：對話框不要疊在登入頁上
    "$route.name"(name) {
      if (this.show && LOGIN_FLOW_ROUTES.includes(name)) this.close();
    },
  },
  methods: {
    onInput(value) {
      if (!value) this.dismiss();
    },
    close() {
      this.$store.commit("CloseLoginDialog");
    },
    /** 使用者自己關掉（X、點外面、返回鍵） */
    dismiss() {
      const fromSwitch = this.dialogState.reload;
      this.close();
      // 切換帳號：已經登出了，原本的頁面可能要登入才看得到 —— 回首頁
      if (fromSwitch) window.location.assign("/");
    },
    onSignedIn() {
      const after = this.after;
      const reload = this.dialogState.reload;
      this.close();
      // 切換帳號：整頁重新載入，頁面上（與 store 各模組）還留著的上一個帳號資料一次清掉
      if (reload) {
        if (after) window.location.assign(after);
        else window.location.reload();
        return;
      }
      this.$notify({ content: "登入成功", status: "success" });
      // 登入完回到頁面最上面。不然留在原頁面時會停在登入前捲到的位置（頁面用登入後的身分重建、
      // 內容不一樣了，看起來像停在奇怪的地方）；前往的頁面跟現在同一頁時路由也不會幫忙捲
      const toTop = () => window.scrollTo({ top: 0, left: 0 });
      if (after) {
        this.$router
          .push(after)
          .catch(() => {})
          .then(() => this.$nextTick(toTop));
        return;
      }
      // 留在原頁面：重新建立這一頁，讓頁面用登入後的身分重新抓資料
      this.$store.commit("BumpRouterView");
      this.$nextTick(toTop);
    },
    /** 登入、註冊、驗證信、忘記密碼在對話框裡切換；其他步驟到登入頁做，做完登入後回到這裡 */
    onSwitch(name) {
      if (DIALOG_VIEWS.includes(name)) {
        this.$store.commit("SetLoginDialogView", name);
        return;
      }
      const after = this.after;
      this.close();
      localStorage.setItem("redirectAfterLogin", after || window.location.href);
      const status = name === "SignUp" ? "signup" : name === "ResetPwd" ? "resetpwd" : "signin";
      const raw = this.$route.query.refc;
      const refc = Array.isArray(raw) ? raw[0] : raw;
      this.$router
        .push({
          name: "SignIn",
          query: { status, from: "lt", ...(typeof refc === "string" && refc.trim() !== "" ? { refc } : {}) },
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.login-dialog {
  position: relative;
}
.login-dialog__close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}
.login-dialog__body {
  padding: 24px 12px 12px !important;
}
</style>
